import { useEffect, useState } from 'react';
import { Backdrop, CircularProgress, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import InputField from '../common/fields/InputField';
import PrimaryButton from '../common/buttons/PrimaryButton';
import { mapApiValidationErrors, getApiErrorMessage } from '../../utils/apiError';
import { createParentStudent } from '../../services/parentStudentService';

const getInitialForm = () => ({
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
});

const validate = (form) => {
  const errors = {};

  if (!form.first_name.trim()) errors.first_name = 'First name is required';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required';
  if (!form.password) errors.password = 'Password is required';
  if (!form.confirm_password) errors.confirm_password = 'Confirm password is required';
  if (form.password && form.confirm_password && form.password !== form.confirm_password) {
    errors.confirm_password = 'Passwords do not match';
  }

  return errors;
};

const ParentStudentRegistrationModal = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState(getInitialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) {
      setForm(getInitialForm());
      setErrors({});
      setLoading(false);
      setSubmitError('');
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));

    if (errors[field]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      const response = await createParentStudent(form);
      onClose();
      onSuccess?.(response);
    } catch (err) {
      if (err?.response?.status === 401) {
        setSubmitError('Your session has expired. Please sign in again.');
        return;
      }

      const mappedErrors = mapApiValidationErrors(err.response?.data);
      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors);
      } else {
        setSubmitError(getApiErrorMessage(err, 'Unable to register student. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0,0,0,0.72)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17,24,39,1) 0%, rgba(15,15,18,1) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '14px',
            maxHeight: '90vh',
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={loading}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'white',
          zIndex: 1,
          '&:hover': { background: 'rgba(229,64,4,0.1)', color: '#F6911D' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <div className="border-b border-white/10 px-6 py-5">
          <h2 className="text-2xl font-semibold text-white">Register New Student</h2>
          <p className="mt-1 text-sm text-white/55">Add a new student account to your parent profile.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6">
          {submitError ? (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
              {submitError}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <InputField
              label="First Name"
              value={form.first_name}
              onChange={handleChange('first_name')}
              required
              disabled={loading}
              error={!!errors.first_name}
              helperText={errors.first_name}
              icon={PersonOutlineOutlinedIcon}
            />
            <InputField
              label="Last Name"
              value={form.last_name}
              onChange={handleChange('last_name')}
              required
              disabled={loading}
              error={!!errors.last_name}
              helperText={errors.last_name}
              icon={PersonOutlineOutlinedIcon}
            />
          </div>

          <InputField
            label="Password"
            type="password"
            value={form.password}
            onChange={handleChange('password')}
            required
            disabled={loading}
            error={!!errors.password}
            helperText={errors.password}
            icon={LockOutlinedIcon}
          />

          <InputField
            label="Confirm Password"
            type="password"
            value={form.confirm_password}
            onChange={handleChange('confirm_password')}
            required
            disabled={loading}
            error={!!errors.confirm_password}
            helperText={errors.confirm_password}
            icon={LockOutlinedIcon}
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-white/70 hover:border-white/20 hover:text-white disabled:opacity-50"
            >
              Cancel
            </button>

            <PrimaryButton type="submit" disabled={loading} sx={{ minWidth: 160 }}>
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Register Student'}
            </PrimaryButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ParentStudentRegistrationModal;
